/**
 * FEATURE FLAGS (aka "Flippers")
 * =================
 * These are flagged-in features that we control.
 * To manage these features, go to: https://admin.github.com/devtools/feature_flags
 */
const featureFlags = [
  // This method demonstrates usage of this service, but it isn't a real feature.
  'memex_beta_with_dummy_feature',

  // Enables access to the Insights tab/view from the navigation buttons,
  // and enables historical Insights 'time' charts
  'memex_insights',

  // Feature flag override to provide memex_charts_basic_public/private gated features
  // typically reserved for paid plans.
  'memex_charts_basic_allow',

  // Whether to show the Group by assignees, milestones for historical charts
  'memex_historical_charts_on_assignees_milestones',

  // Tasks list block gate the tracks column and all things related to hierarchy
  'tasklist_block',

  // Enable "group by distinct" for multi-value fields like assignees
  'memex_group_by_multi_value_changes',

  // Enables the redesigned tracked by pills and hovercards
  'tasklist_tracked_by_redesign',

  // Enables a paginated archive view to support a 48K archive limit
  'memex_paginated_archive',

  // Enables the resync index button for project admins for reindexing Elasticsearch for Memex Without limits
  'memex_resync_index',

  // Controls whether automations are enabled or not, this is not a real FF, it delegates to enterprise business config
  'memex_automation_enabled',

  // Whether to use the new header or not
  'memex_header_redesign',

  // Enable Table View Without Limits (Elasticsearch powered backend)
  'memex_table_without_limits',

  // Enable the paginate items query independently of the memex_without_limits flag
  'memex_multi_page_query',

  // Enable the new auto-close workflow
  'memex_autoclose_issue_on_status_change_workflow',

  // Enable the new bulk actions endpoint
  'memex_bulk_actions_background_job',

  // New issue viewer from the @github-ui/issue-viewer package
  'memex_new_issue_viewer',

  // If we should prefetch issue data for the new viewer on hover
  'memex_issue_viewer_prefetch',

  // Disable file upload in the side-panel for draft issues
  'memex_disable_draft_issue_file_upload',

  // Enable live updates for the issue viewer
  'hyperlist_issue_live_updates',

  'graphql_subscriptions',

  'issues_react_issue_viewer_timeline_secondary_fetch',
  'issues_react_issue_viewer_prefetch_interaction_data_enabled',

  // Sort and filter table items without react-table plugins
  'memex_prefilter_table_items',

  // Disable autofocusing first item in project views
  'memex_disable_autofocus',

  // Enable the new template dialog design/experience
  'memex_new_template_experience',

  // Enable the issue_types picker in the issue create dialog
  'issue_types',

  // Enable dropdown for single select options
  'memex_single_select_dropdown',

  // Enable the instructions modal for reordering list items
  'memex_reorder_keyboard_instructions',

  // Enable project status updates
  'memex_status_updates',

  // Enable project status updates
  'memex_status_updates_notifications',

  // Enable keyboard drag and drop for single select fields
  'memex_single_select_keyboard_dnd',

  // Display Copilot Workspace button in issue sidebar
  'copilot_workspace',
] as const

type FeatureFlags = (typeof featureFlags)[number]

/**
 * FEATURE PREVIEWS
 * =================
 * These are opt-in features available to users.
 * To manage these features, go to: https://admin.github.com/devtools/toggleable_features
 */
const featurePreviews = [] as const

type FeaturePreviews = (typeof featurePreviews)[number]

/**
 * FEATURE GATES
 * =================
 * These are features gated by the billing plan of the memex owner org/user.
 * Feature gates are specified in plans.yaml files, as described here:
 * https://github.com/github/githubber-content/blob/691749f4be532371dbb7aa65a8b2440e68f6e673/docs/technology/dotcom/plan-feature-gates.md
 */
const featureGates = [
  // Enables basic features for creating/saving custom, current state charts in the project insights view for public projects
  'memex_charts_basic_public',
  // Enables basic features for creating/saving custom, current state charts in the project insights view for private projects
  'memex_charts_basic_private',

  // Enables limited/taste/preview features for viewing/creating/saving historical Insights charts in the project insights view for public projects
  'memex_insights_limited_public',
  // Enables limited/taste/preview features for viewing/creating/saving historical Insights charts in the project insights view for private projects
  'memex_insights_limited_private',

  // Enables basic features for viewing/creating/saving historical Insights charts in the project insights view for public projects
  'memex_insights_basic_public',
  // Enables basic features for viewing/creating/saving historical Insights charts in the project insights view for private projects
  'memex_insights_basic_private',
] as const

type FeatureGates = (typeof featureGates)[number]

/**
 * ENABLED FEATURES
 * =================
 * Projects don't need to know where a feature is enabled from, only the state.
 * Given that, we merge all feature flags, previews, and gates into a single array.
 */
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export type EnabledFeatures = FeaturePreviews | FeatureFlags | FeatureGates
export const enabledFeatures = [...featurePreviews, ...featureFlags, ...featureGates]

export type EnabledFeaturesMap = {[P in EnabledFeatures]: boolean}

export const allFeaturesDisabled = enabledFeatures.reduce((map, current) => {
  map[current] = false
  return map
}, {} as EnabledFeaturesMap)
